/**
 * Reglas puras del modo mantenimiento: a quién se le muestra la pantalla y a quién no.
 *
 * El corte es AL ENTRAR: quien llega nuevo a la cartelera, a un evento o al panel ve la
 * pantalla; quien ya venía comprando termina su compra. Lo decide el navegador con lo
 * que tiene a mano: el estado del servidor (`active`), la sesión (super admin pasa) y lo
 * que el checkout y la taquilla ya guardan en `localStorage` mientras arman una venta.
 */

export const MAINTENANCE_ROUTE = '/mantenimiento';

/**
 * Rutas que nunca se bloquean, aunque haya mantenimiento:
 * - `/auth`: el equipo tiene que poder iniciar sesión para desactivarlo.
 * - `/mantenimiento`: la pantalla misma.
 * - `/sale-validate` y `/ticket`: validación en puerta y PDF del boleto; no son compras.
 * - `/thank-you` y `/mis-boletos`: la vuelta de la pasarela y los boletos de una compra
 *   que ya se hizo. Cortar ahí dejaría al comprador sin su confirmación.
 */
export const MAINTENANCE_EXEMPT_PREFIXES: readonly string[] = [
  '/auth',
  MAINTENANCE_ROUTE,
  '/sale-validate',
  '/ticket',
  '/thank-you',
  '/mis-boletos',
  '/privacy',
  '/terminos-condiciones',
];

/**
 * Parámetros con los que Mercado Pago, Stripe y PayPal devuelven al comprador a la app.
 * Si la URL trae alguno, esa persona viene de pagar: se le deja llegar aunque su
 * temporizador de compra haya vencido mientras estaba en la pasarela.
 */
export const GATEWAY_RETURN_PARAMS: readonly string[] = [
  'payment_id',
  'collection_id',
  'preference_id',
  'merchant_order_id',
  'external_reference',
  'session_id',
  'token',
  'PayerID',
  'paymentId',
];

/**
 * Claves que `TimerSessionService` escribe al elegir función: marcan el inicio de la
 * compra. Las tres juntas: un temporizador sin slug o sin `session-event-{slug}` está
 * huérfano y el propio servicio lo descarta al restaurar, así que acá tampoco cuenta.
 */
export const CHECKOUT_TIMER_KEY = 'session-timer-expiresAt';
export const CHECKOUT_TIMER_SLUG_KEY = 'session-timer-slugUrl';
export const checkoutSessionKey = (slug: string): string => `session-event-${slug}`;

/** Prefijos con los que la taquilla (mapa y sin mapa) guarda la venta a medio armar. */
export const TICKET_OFFICE_SALE_PREFIXES: readonly string[] = [
  'ticket-office-current-sale[',
  'ticket-office-any-tipo-current-sale[',
];

/** Lo mínimo de `Storage` que se usa; permite probar sin navegador. */
export interface StorageLike {
  readonly length: number;
  key(index: number): string | null;
  getItem(key: string): string | null;
}

export interface MaintenanceDecisionInput {
  active: boolean;
  isSuperAdmin: boolean;
  url: string;
  storage: StorageLike | null | undefined;
  now?: number;
}

export function pathOf(url: string): string {
  const sinHash = String(url ?? '').split('#')[0];
  const path = sinHash.split('?')[0] || '/';
  return path.startsWith('/') ? path : `/${path}`;
}

export function isMaintenanceExemptUrl(url: string): boolean {
  const path = pathOf(url);
  return MAINTENANCE_EXEMPT_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
}

export function hasGatewayReturnParams(url: string): boolean {
  const query = String(url ?? '').split('#')[0].split('?')[1];
  if (!query) return false;
  const params = new URLSearchParams(query);
  return GATEWAY_RETURN_PARAMS.some((name) => params.has(name));
}

/** Compra online en curso: temporizador vigente y sesión de compra detrás (misma regla que `restoreTimer`). */
export function checkoutInProgress(storage: StorageLike | null | undefined, now: number = Date.now()): boolean {
  const raw = safeGet(storage, CHECKOUT_TIMER_KEY);
  if (!raw) return false;
  const expiresAt = Date.parse(raw);
  if (!Number.isFinite(expiresAt) || expiresAt <= now) return false;
  const slug = safeGet(storage, CHECKOUT_TIMER_SLUG_KEY);
  return !!slug && !!safeGet(storage, checkoutSessionKey(slug));
}

/** Venta de taquilla en curso: hay una venta guardada con al menos un boleto elegido. */
export function ticketOfficeSaleInProgress(storage: StorageLike | null | undefined): boolean {
  if (!storage) return false;
  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i);
    if (!key || !TICKET_OFFICE_SALE_PREFIXES.some((prefix) => key.startsWith(prefix))) continue;
    if (saleHasTickets(safeGet(storage, key))) return true;
  }
  return false;
}

export function purchaseInProgress(storage: StorageLike | null | undefined, now: number = Date.now()): boolean {
  return checkoutInProgress(storage, now) || ticketOfficeSaleInProgress(storage);
}

/**
 * ¿Hay que mostrar la pantalla de mantenimiento en esta navegación?
 * Orden: sin mantenimiento no; super admin no; ruta exenta no; vuelta de pasarela no;
 * compra o venta en curso no; todo lo demás sí.
 */
export function shouldShowMaintenance(input: MaintenanceDecisionInput): boolean {
  if (!input.active) return false;
  if (input.isSuperAdmin) return false;
  if (isMaintenanceExemptUrl(input.url)) return false;
  if (hasGatewayReturnParams(input.url)) return false;
  if (purchaseInProgress(input.storage, input.now ?? Date.now())) return false;
  return true;
}

/**
 * Adónde volver cuando termina el mantenimiento. Solo rutas internas: nada de `//host`
 * ni URLs absolutas, para que el parámetro no sirva para sacar a nadie del sitio.
 */
export function safeReturnUrl(candidate: unknown): string {
  const value = String(candidate ?? '').trim();
  if (!value.startsWith('/') || value.startsWith('//') || value.startsWith('/\\')) return '/';
  if (pathOf(value) === MAINTENANCE_ROUTE) return '/';
  return value;
}

function safeGet(storage: StorageLike | null | undefined, key: string): string | null {
  try {
    return storage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function saleHasTickets(raw: string | null): boolean {
  if (!raw) return false;
  try {
    const sale = JSON.parse(raw) as { functions?: { selectedZones?: { seats?: unknown[]; quantity?: unknown }[] }[] };
    return (sale?.functions ?? []).some((f) =>
      (f?.selectedZones ?? []).some((z) => (z?.seats?.length ?? 0) > 0 || Number(z?.quantity) > 0),
    );
  } catch {
    return false;
  }
}
